import { getApiBaseUrl } from "../env";
import { getVisitorPostBody } from "./webTrack";

type ClientAlertType =
  | "boot_timeout"
  | "js_error"
  | "unhandled_rejection"
  | "chunk_load_error"
  | "afisha_load_error";

const sentKeys = new Set<string>();
let bootSucceeded = false;
let bootWatchScheduled = false;

const BOOT_TIMEOUT_MS = 10_000;

function sendAlertOnce(
  key: string,
  payload: {
    type: ClientAlertType;
    message?: string;
    extra?: Record<string, unknown>;
  },
) {
  if (sentKeys.has(key)) {
    return;
  }
  sentKeys.add(key);

  const body = JSON.stringify({
    ...payload,
    url: window.location.href,
    ...getVisitorPostBody(),
  });

  const url = `${getApiBaseUrl()}/api/channels/client-alert`;

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(url, blob)) {
      return;
    }
  }

  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function markAfishaBootSuccess(): void {
  bootSucceeded = true;
}

export function initClientAlertMonitoring(): void {
  if (typeof window === "undefined" || bootWatchScheduled) {
    return;
  }
  bootWatchScheduled = true;

  window.addEventListener(
    "error",
    (event) => {
      const message = event.message || "Unknown error";
      const isChunk =
        /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(
          message,
        ) ||
        /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(
          String(event.error?.message ?? ""),
        );

      sendAlertOnce(`js:${message}`, {
        type: isChunk ? "chunk_load_error" : "js_error",
        message: `${message}\n${event.filename || ""}:${event.lineno || ""}`,
      });
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason =
      event.reason instanceof Error
        ? event.reason.stack || event.reason.message
        : String(event.reason ?? "unknown rejection");

    sendAlertOnce(`rej:${reason.slice(0, 200)}`, {
      type: "unhandled_rejection",
      message: reason,
    });
  });

  window.setTimeout(() => {
    if (bootSucceeded) {
      return;
    }

    const root = document.getElementById("root");
    const hasAfishaShell = Boolean(document.querySelector(".afisha"));
    const rootEmpty = !root?.innerHTML?.trim();

    sendAlertOnce("boot_timeout", {
      type: "boot_timeout",
      message: rootEmpty
        ? "React root пустой — приложение не смонтировалось"
        : hasAfishaShell
          ? "Афиша не загрузила данные за отведённое время"
          : "Страница не перешла в состояние афиши",
      extra: {
        rootEmpty,
        hasAfishaShell,
      },
    });
  }, BOOT_TIMEOUT_MS);
}

export function reportAfishaLoadError(message: string): void {
  sendAlertOnce(`afisha_load:${message}`, {
    type: "afisha_load_error",
    message,
  });
}
